<template>
  <div>
    <nav>
      <ul class="pager">
        <li class="previous">
          <router-link :to="{ name: 'client_dashboard', client_id: $route.params.client_id }">
            <span aria-hidden="true">&larr;</span> Client dashboard
          </router-link>
        </li>
      </ul>
    </nav>
    <div v-if="fetching">
      <h1>Dry Cow Summary Report</h1>
      <h4>This may take a few minutes</h4>
      <loader></loader>
    </div>
    <div v-else>
      <h1>Key Vet Dry Cow {{ model.Misc.season_start_year }} Summary Report</h1>

      <div class="sections">
        <br />
        <div class="form-group">
          <h2 class="text-left alert alert-success">Introduction</h2>
          <textarea rows="5" cols="50" v-model="model['Misc']['summary_keypoints']" class="form-control"> </textarea>
        </div>

        <br />
        <client-details :model="model['ClientDetails']" @onRemoveSection="removeSection"> </client-details>
        <hr />

        <current-mastitis-situation
          :model="model['CurrentMastitisSituation']"
          @onRemoveSection="removeSection"
          ref="currentMastitisSituation"
        >
        </current-mastitis-situation>
        <hr />

        <mastitis-goals :model="model['MastitisGoals']" @onRemoveSection="removeSection"> </mastitis-goals>
        <hr />

        <mastitis-control-measures :model="model['MastitisControlMeasures']" @onRemoveSection="removeSection">
        </mastitis-control-measures>
        <hr />

        <cow-environment :model="model['CowEnvironment']" @onRemoveSection="removeSection"> </cow-environment>
        <hr />

        <dry-cow-strategy :model="model['DryCowStrategy']" @onRemoveSection="removeSection"> </dry-cow-strategy>
        <hr />

        <preventive-health-and-vaccination
          :model="model['PreventiveHealthAndVacination']"
          @onRemoveSection="removeSection"
        >
        </preventive-health-and-vaccination>
        <hr />

        <dry-off-process :model="model['DryOffProcess']" @onRemoveSection="removeSection"> </dry-off-process>
        <hr />

        <this-season-dry-off :model="model['ThisSeasonDryOff']" @onRemoveSection="removeSection"> </this-season-dry-off>
        <hr />

        <div class="form-group">
          <h2 class="text-left alert alert-success">Final comments</h2>
          <textarea rows="5" cols="50" v-model="model['Misc'].summary_final_comment" class="form-control"> </textarea>
        </div>

        <button v-if="!generatingPDF" class="btn btn-success" v-on:click="generateReport">Generate PDF</button>

        <loader v-if="generatingPDF"></loader>
      </div>
    </div>
  </div>
</template>

<script>
import { BASE_API_URL, consultFormService } from '../../_services'
import { DryCowSummaryComponents } from '../../components/reports/dc_summary'
import { authHeader, handleResponse, scrollToTop } from '@/_helpers'
import axios from 'axios'

const DEFAULT_SETTINGS = {
  ClientDetails: { removed: false },
  CurrentMastitisSituation: { removed: false },
  MastitisGoals: { removed: false },
  MastitisControlMeasures: { removed: false },
  CowEnvironment: { removed: false },
  DryCowStrategy: { removed: false },
  PreventiveHealthAndVacination: { removed: false },
  DryOffProcess: { removed: false },
  ThisSeasonDryOff: { removed: false },
  DryCowAndTeatsealOrder: { removed: false },
}

export default {
  components: {
    ...DryCowSummaryComponents,
  },
  data() {
    return {
      model: {},
      clientId: null,
      season: null,
      fetching: true,
      generatingPDF: false,
      removedSections: this.getDefaultSections(),
    }
  },
  mounted() {
    scrollToTop()
    this.clientId = this.$route.params.client_id
    this.season = this.$route.params.season

    this.fetching = true

    consultFormService
      .getSummaryReportSettings({ clientId: this.clientId, consultType: 'drycow_consult' })
      .then((data) => {
        this.removedSections = data.settings
      })

    let endpoint = '/clients/' + this.clientId + '/dry_cow_consult/' + this.season

    consultFormService.getClientConsultData(endpoint).then(
      (data) => {
        this.model = data
        this.fetching = false
      },
      (/* error */) => {
        this.fetching = false
        this.$store.dispatch('alert/error', { message: 'An error occurred. Contact the administrator.' })
      }
    )
  },
  methods: {
    resetSettings: function () {
      this.removedSections = this.getDefaultSections()
    },
    getDefaultSections: function () {
      return Object.assign({}, DEFAULT_SETTINGS)
    },
    addChartsToModel: function () {
      if (!this.removedSections['CurrentMastitisSituation'].removed) {
        this.model['CurrentMastitisSituation']['CHARTS_b64'] = {}

        for (var farm of this.model['CurrentMastitisSituation'].FARMS) {
          let key = 'farm_' + farm.id + '-mastitis_treatments_chart'

          if (this.$refs.currentMastitisSituation.$refs[key][0]) {
            this.model['CurrentMastitisSituation']['CHARTS_b64'][key] =
              this.$refs.currentMastitisSituation.$refs[key][0].$refs.canvas.toDataURL()
          }
        }
      }
    },
    generateReport: function () {
      // let endpoint = BASE_API_URL+'/reports/dc_summary/'+this.clientId+'/export_pdf/'+this.season
      let endpoint = `${BASE_API_URL}/reports/dc_summary/${this.clientId}/export_pdf/${this.season}`

      this.addChartsToModel()
      this.generatingPDF = true

      return axios({
        method: 'POST',
        url: endpoint,
        headers: authHeader(),
        responseType: 'blob',
        data: {
          model: this.model,
          configuration: this.removedSections,
        },
      })
        .catch(handleResponse)
        .then(
          (response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', this.clientId + '_dry_cow_summary_report.pdf')
            document.body.appendChild(link)
            link.click()
            link.remove()
            this.generatingPDF = false
          },
          (/* error */) => {
            window.scrollTo(0, 0)

            this.$store.dispatch('alert/error', { message: 'An error occurred. Contact the administrator.' })
            this.generatingPDF = false
          }
        )
    },
    removeSection: function (section, subsection) {
      if (subsection) {
        if (this.removedSections[section][subsection] == undefined) {
          this.$set(this.removedSections[section], subsection, true)
        } else {
          this.$set(this.removedSections[section], subsection, !this.removedSections[section][subsection])
        }
      } else {
        this.removedSections[section]['removed'] = !this.removedSections[section]['removed'] || false
      }
    },
  },
}
</script>

<style>
dd {
  text-align: left;
}
.sections {
  padding-left: 20px;
}
.glyphicon-remove {
  color: red;
}
.glyphicon-plus {
  color: #5cb85c;
}
.dl-horizontal dt {
  white-space: normal;
}
.subsection-disabled {
  text-decoration: line-through;
}

div.step-section {
  margin: 50px 5px;
}
</style>
